// Modal: shows the GPS trace for a daily report on a Google Map
// with markers for pickups, deliveries and pauses.
import { useEffect, useRef, useState } from 'react';
import { X, MapPin, Loader2, Activity } from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '../hooks/useApi';
import { formatStopDateTime } from '../lib/dateFormat';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const loadGoogleMaps = () => new Promise((resolve, reject) => {
  if (window.google?.maps) return resolve(window.google.maps);
  const existing = document.getElementById('gmaps-js');
  if (existing) {
    existing.addEventListener('load', () => resolve(window.google.maps));
    existing.addEventListener('error', reject);
    return;
  }
  const s = document.createElement('script');
  s.id = 'gmaps-js';
  s.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=geometry`;
  s.async = true;
  s.defer = true;
  s.onload = () => resolve(window.google.maps);
  s.onerror = reject;
  document.head.appendChild(s);
});

const stopColor = {
  pickup:     '#3b82f6',
  delivery:   '#10b981',
  pause:      '#f59e0b',
  fin_trajet: '#94a3b8',
};

export default function ReportGpsMapModal({ open, onClose, reportId, reportNumber }) {
  const { get } = useApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  // Fetch GPS trace when opened
  useEffect(() => {
    if (!open || !reportId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    (async () => {
      try {
        // Cache-buster ?_t=... pour bypasser tout HTTP cache navigateur
        // qui pourrait retourner une ancienne réponse vide (bug reporté 09/2026).
        const res = await get(`/daily-reports/${reportId}/gps-trace?_t=${Date.now()}`);
        const payload = res?.data || res;
        if (!cancelled) setData(payload);
      } catch (e) {
        if (!cancelled) {
          const msg = e.response?.data?.detail || e.message || 'Erreur inconnue';
          setError(msg);
          toast.error(`Carte indisponible : ${msg}`);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, reportId, get]);

  // Render the map once data is loaded
  useEffect(() => {
    if (!open || !data || !mapRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const maps = await loadGoogleMaps();
        if (cancelled) return;

        if (!mapInstanceRef.current) {
          mapInstanceRef.current = new maps.Map(mapRef.current, {
            center: { lat: 46.5, lng: -72.5 },
            zoom: 7,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            styles: [
              { elementType: 'geometry', stylers: [{ color: '#1e293b' }] },
              { elementType: 'labels.text.stroke', stylers: [{ color: '#0f172a' }] },
              { elementType: 'labels.text.fill', stylers: [{ color: '#94a3b8' }] },
              { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0c4a6e' }] },
              { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#334155' }] },
            ],
          });
        }
        const map = mapInstanceRef.current;
        const bounds = new maps.LatLngBounds();

        // Polyline of GPS trace
        const path = (data.trace || []).map(p => ({ lat: p.lat, lng: p.lng }));
        if (path.length >= 2) {
          new maps.Polyline({
            path,
            strokeColor: '#f59e0b',
            strokeOpacity: 0.9,
            strokeWeight: 4,
            map,
          });
          path.forEach(p => bounds.extend(p));
        }

        // Stop markers (geocode each stop's address via Google Geocoder if needed)
        const geocoder = new maps.Geocoder();
        for (const [i, s] of (data.stops || []).entries()) {
          if (!s.address) continue;
          try {
            const result = await new Promise((resolve, reject) => {
              geocoder.geocode({ address: s.address + ', QC, Canada' }, (res, status) => {
                if (status === 'OK' && res?.[0]) resolve(res[0].geometry.location);
                else reject(status);
              });
            });
            const color = stopColor[s.stop_type] || stopColor.delivery;
            new maps.Marker({
              position: { lat: result.lat(), lng: result.lng() },
              map,
              label: { text: String(i + 1), color: '#fff', fontWeight: 'bold', fontSize: '11px' },
              title: `${s.client_name || s.address}`,
              icon: {
                path: maps.SymbolPath.CIRCLE,
                scale: 12,
                fillColor: color,
                fillOpacity: 0.95,
                strokeColor: '#0f172a',
                strokeWeight: 2,
              },
            });
            bounds.extend({ lat: result.lat(), lng: result.lng() });
          } catch {
            // skip ungeocodable stops
          }
        }

        // Fit bounds
        if (!bounds.isEmpty()) map.fitBounds(bounds, 60);
      } catch (e) {
        console.error('Map render failed', e);
      }
    })();
    return () => { cancelled = true; };
  }, [open, data]);

  if (!open) return null;

  const trace = data?.trace || [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-3 sm:p-4"
      data-testid="gps-trace-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
              <MapPin className="w-5 h-5 text-amber-400" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-bold text-white truncate">{reportNumber}</h2>
              <p className="text-xs text-slate-400">Trajet GPS parcouru</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white"
            data-testid="close-gps-modal"
            type="button"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {loading && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
              <p className="text-slate-300 text-sm">Chargement du trajet…</p>
            </div>
          )}

          {error && !loading && (
            <div className="m-4 bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-300">
              <p className="text-sm">{error}</p>
            </div>
          )}

          {data && !loading && (
            <>
              {/* Stats bar */}
              <div className="grid grid-cols-3 px-4 py-2 border-b border-slate-800 text-center text-xs">
                <div>
                  <p className="text-amber-400 font-bold text-base">{data.point_count || 0}</p>
                  <p className="text-slate-500 uppercase tracking-wider">Points GPS</p>
                </div>
                <div className="border-x border-slate-800">
                  <p className="text-amber-400 font-bold text-base">{(data.stops || []).length}</p>
                  <p className="text-slate-500 uppercase tracking-wider">Arrêts</p>
                </div>
                <div>
                  <p className="text-amber-400 font-bold text-base">
                    {trace.length > 0
                      ? formatStopDateTime(trace[0].ts).split(' · ')[0]
                      : '—'}
                  </p>
                  <p className="text-slate-500 uppercase tracking-wider">Date</p>
                </div>
              </div>

              {/* Map */}
              <div
                ref={mapRef}
                className="flex-1 min-h-[300px] sm:min-h-[450px] bg-slate-800"
                data-testid="gps-map-canvas"
              />

              {trace.length === 0 && (
                <div className="px-4 py-3 bg-amber-500/10 border-t border-amber-500/30 flex items-start gap-2">
                  <Activity className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                  <p className="text-amber-200 text-xs leading-tight">
                    Aucune position GPS enregistrée pour ce rapport. Les arrêts sont affichés s&apos;ils peuvent être géocodés.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
